import { useState, useEffect } from "react";
import { Modal, Form, Input, Switch, message, Button, Space } from "antd";
import { FormatPainterOutlined, FullscreenOutlined, FullscreenExitOutlined } from "@ant-design/icons";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { oneDark } from "@codemirror/theme-one-dark";
import { EditorView } from "@codemirror/view";
import { fetchBin, updateBin, updateBinName, createBin, getToken } from "../utils/api";
import { useLocale } from "../contexts/LocaleContext";
import { useTheme } from "../contexts/ThemeContext";

interface BinEditorModalProps {
  open: boolean;
  binId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function BinEditorModal({
  open,
  binId,
  onClose,
  onSuccess,
}: BinEditorModalProps) {
  const { t } = useLocale();
  const { theme } = useTheme();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalName, setOriginalName] = useState<string>("");
  const [jsonValue, setJsonValue] = useState<string>("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (open) {
      if (binId) {
        loadBin();
      } else {
        const defaultJson = "{}";
        form.resetFields();
        form.setFieldsValue({ data: defaultJson, private: true });
        setJsonValue(defaultJson);
        setOriginalName("");
      }
    } else {
      setIsFullscreen(false);
    }
  }, [open, binId, form]);

  const loadBin = async () => {
    if (!binId) return;

    setLoading(true);
    try {
      const bin = await fetchBin(binId);
      const name = bin.metadata.name || "";
      const formattedJson = JSON.stringify(bin.record, null, 2);
      setOriginalName(name);
      setJsonValue(formattedJson);
      form.setFieldsValue({
        name: name,
        data: formattedJson,
      });
    } catch (err) {
      message.error(err instanceof Error ? err.message : t.binEditor.loadFailed);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleFormat = () => {
    try {
      const currentValue = jsonValue || form.getFieldValue("data");
      if (!currentValue) return;
      
      const parsed = JSON.parse(currentValue);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonValue(formatted);
      form.setFieldValue("data", formatted);
      message.success(t.binEditor.formatSuccess);
    } catch (err) {
      message.error(t.binEditor.jsonError);
    }
  };

  const handleJsonChange = (value: string) => {
    setJsonValue(value);
    form.setFieldValue("data", value);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = getToken();
      if (!token) {
        message.warning(t.binEditor.tokenWarning);
        return;
      }

      let jsonData;
      try {
        jsonData = JSON.parse(values.data);
      } catch (err) {
        message.error(t.binEditor.jsonError);
        return;
      }

      setSaving(true);
      if (binId) {
        await updateBin(binId, jsonData);
        if (values.name !== originalName) {
          if (values.name && values.name.trim()) {
            await updateBinName(binId, values.name.trim());
          }
        }
        message.success(t.binEditor.updateSuccess);
      } else {
        await createBin(jsonData, values.name || undefined, values.private !== false);
        message.success(t.binEditor.createSuccess);
      }
      onSuccess();
      onClose();
    } catch (err) {
      message.error(err instanceof Error ? err.message : t.binEditor.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {isFullscreen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            backgroundColor: theme === "dark" || theme === "tech" ? "#1E293B" : "#FFFFFF",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "16px",
              borderBottom: `1px solid ${theme === "dark" || theme === "tech" ? "#334155" : "#E2E8F0"}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontWeight: 500, color: theme === "dark" || theme === "tech" ? "#FFFFFF" : "#1E293B" }}>
              {t.binEditor.dataLabel}
            </span>
            <Space>
              <Button
                type="link"
                size="small"
                icon={<FormatPainterOutlined />}
                onClick={handleFormat}
              >
                {t.binEditor.format}
              </Button>
              <Button
                type="text"
                icon={<FullscreenExitOutlined />}
                onClick={toggleFullscreen}
              >
                {t.binEditor.exitFullscreen}
              </Button>
            </Space>
          </div>
          <div className="codemirror-fullscreen-wrapper">
            <CodeMirror
              value={jsonValue}
              height="calc(100vh - 80px)"
              extensions={[
                json(),
                EditorView.lineWrapping,
                EditorView.theme({
                  "&": {
                    fontSize: "14px",
                  },
                  ".cm-content": {
                    fontFamily: "Monaco, Menlo, 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace",
                  },
                }),
              ]}
              theme={theme === "dark" || theme === "tech" ? oneDark : undefined}
              onChange={handleJsonChange}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                dropCursor: false,
                allowMultipleSelections: false,
                syntaxHighlighting: true,
                highlightSelectionMatches: true,
              }}
            />
          </div>
        </div>
      )}
      <Modal
        title={binId ? t.binEditor.editTitle : t.binEditor.createTitle}
        open={open}
        onOk={handleSave}
        onCancel={onClose}
        okText={binId ? t.common.update : t.common.create}
        cancelText={t.common.cancel}
        width={800}
        confirmLoading={saving}
        loading={loading}
      >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label={t.binEditor.nameLabel}
          rules={[
            {
              validator: (_, value) => {
                if (!value || !value.trim()) {
                  return Promise.resolve();
                }
                if (!/^[\x00-\x7F]*$/.test(value.trim())) {
                  return Promise.reject(new Error(t.binEditor.nameAsciiError));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder={t.binEditor.namePlaceholder} />
        </Form.Item>
        {!binId && (
          <Form.Item
            name="private"
            label={t.binEditor.privateLabel}
            valuePropName="checked"
            tooltip={t.binEditor.privateTooltip}
          >
            <Switch checkedChildren={t.common.yes} unCheckedChildren={t.common.no} />
          </Form.Item>
        )}
        <Form.Item
          name="data"
          label={
            <Space>
              <span>{t.binEditor.dataLabel}</span>
              <Button
                type="link"
                size="small"
                icon={<FormatPainterOutlined />}
                onClick={handleFormat}
                style={{ padding: 0, height: "auto" }}
              >
                {t.binEditor.format}
              </Button>
              <Button
                type="link"
                size="small"
                icon={<FullscreenOutlined />}
                onClick={toggleFullscreen}
                style={{ padding: 0, height: "auto" }}
              >
                {t.binEditor.fullscreen}
              </Button>
            </Space>
          }
          rules={[
            { required: true, message: t.binEditor.dataPlaceholder },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                try {
                  JSON.parse(value);
                  return Promise.resolve();
                } catch {
                  return Promise.reject(new Error(t.binEditor.jsonError));
                }
              },
            },
          ]}
        >
          <div 
            style={{ 
              border: "1px solid #d9d9d9", 
              borderRadius: "6px", 
              overflow: "hidden",
              isolation: "isolate"
            }}
            className="codemirror-wrapper"
          >
            <CodeMirror
              value={jsonValue}
              height={isFullscreen ? "calc(100vh - 250px)" : "400px"}
              extensions={[
                json(),
                EditorView.lineWrapping,
                EditorView.theme({
                  "&": {
                    fontSize: "14px",
                  },
                  ".cm-content": {
                    fontFamily: "Monaco, Menlo, 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace",
                  },
                }),
              ]}
              theme={theme === "dark" || theme === "tech" ? oneDark : undefined}
              onChange={handleJsonChange}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                dropCursor: false,
                allowMultipleSelections: false,
                syntaxHighlighting: true,
                highlightSelectionMatches: true,
              }}
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>
    </>
  );
}
